import React, { useCallback, useEffect, useState } from "react";
// redux
import { useSelector } from "react-redux";
import { selectClassroom } from "store/reducers/classroom/selectors";
import { selectUser } from "store/reducers/user/selectors";
// hooks
import { useStandingEnabled, useStandingOrder } from "hooks/useStandingOrder";
// components
import AsideWrapper from "components/Aside/AsideWrapper";
import Loader from "components/Loader";
import SelectClassroom from "components/SelectClassroom";
import StandingOrderTabs from "components/StandingOrderTabs";
import Toggle from "components/StandingOrder/Toggle";
import MealTicks from "components/StandingOrder/MealTicks";
import NeedsGrid from "components/StandingOrder/NeedsGrid";
import OwnNumbersSwitch from "components/StandingOrder/OwnNumbersSwitch";
import CopyToRoom from "components/StandingOrder/CopyToRoom";
import { savedMessage } from "components/StandingOrder/format";
import SaveDialog from "components/StandingOrder/SaveDialog";
// utils
import { listAnd, MEAL_KEYS, MEAL_NAME, WEEKDAYS, DIETS } from "components/StandingOrder/format";
// types
import {
  TGrid,
  TMealKey,
  TPreview,
  TSaveResult,
  TStandingBody,
  TStandingOrder,
} from "components/StandingOrder/types";
// styles
import classNames from "classnames/bind";
import styles from "components/StandingOrder/styles.module.scss";

const cn = classNames.bind(styles);

type TDraft = {
  enabled: boolean;
  meals: TMealKey[];
  orderable: TMealKey[];
  shared: TGrid;
  own: { [meal: string]: TGrid };
};

/** Every weekday and need present, missing ones as 0. */
const fullGrid = (g?: TGrid | null): TGrid => {
  const out: TGrid = {};
  WEEKDAYS.forEach(([d]) => {
    out[d] = {};
    DIETS.forEach(([need]) => {
      out[d][need] = (g && g[d] && g[d][need]) || 0;
    });
  });
  return out;
};

const toDraft = (s: TStandingOrder): TDraft => {
  const own: { [meal: string]: TGrid } = {};
  MEAL_KEYS.forEach((m) => {
    const g = s.counts && s.counts[m];
    if (g) own[m] = fullGrid(g);
  });
  return {
    enabled: !!s.enabled,
    meals: s.meals || [],
    orderable: s.orderableMeals || [],
    shared: fullGrid(s.counts && s.counts.ALL),
    own,
  };
};

const toBody = (d: TDraft, overwriteChanged: boolean): TStandingBody => {
  const counts: any = { ALL: d.shared };
  MEAL_KEYS.forEach((m) => {
    counts[m] = d.own[m] && d.orderable.indexOf(m) >= 0 ? d.own[m] : null;
  });
  return {
    enabled: d.enabled,
    // only meals the room can order today
    meals: MEAL_KEYS.filter(
      (m) => d.meals.indexOf(m) >= 0 && d.orderable.indexOf(m) >= 0
    ),
    counts,
    overwriteChanged,
  };
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const StandingOrder: React.FC = () => {
  const enabled = useStandingEnabled();
  const classroom = useSelector(selectClassroom);
  const { customer } = useSelector(selectUser);
  const api = useStandingOrder();
  const { getStanding, previewStanding, saveStanding, copyStanding } = api;

  const roomId = classroom && classroom.value;
  const roomName = (classroom && classroom.label) || "this room";

  const [draft, setDraft] = useState<TDraft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<{
    body: TStandingBody;
    preview: TPreview;
  } | null>(null);
  const [copiedFrom, setCopiedFrom] = useState<{ [roomId: string]: string }>({});

  const load = useCallback(async () => {
    if (!roomId) return;
    try {
      const s = await getStanding(roomId);
      setDraft(toDraft(s));
      setDirty(false);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message);
    }
  }, [roomId, getStanding]);

  useEffect(() => {
    setDraft(null);
    setError(null);
    setMessage(null);
    setWarning(null);
    setPending(null);
    if (enabled) load();
  }, [enabled, load]);

  const edit = (change: (d: TDraft) => TDraft) => {
    setDraft((d) => (d ? change(d) : d));
    setDirty(true);
    setMessage(null);
  };

  const setCount = (
    scope: string,
    weekday: string,
    need: string,
    value: number
  ) =>
    edit((d) => {
      const base = scope === "shared" ? d.shared : d.own[scope];
      const next = { ...base, [weekday]: { ...base[weekday], [need]: value } };
      return scope === "shared"
        ? { ...d, shared: next }
        : { ...d, own: { ...d.own, [scope]: next } };
    });

  const finish = async (
    body: TStandingBody,
    preview: TPreview | null,
    overwrite: boolean
  ) => {
    const res: TSaveResult = await saveStanding(roomId!, {
      ...body,
      overwriteChanged: overwrite,
    });
    const days =
      res && res.filled ? res.filled.days : preview ? preview.daysToUpdate : 0;
    const kept = preview ? preview.changedMeals.length : 0;
    setMessage(
      savedMessage({
        enabled: !!body.enabled,
        days,
        kept: preview ? kept : 0,
        overwrite,
        roomName,
      })
    );
    const needs = (res && res.needsYou) || [];
    setWarning(
      needs.length
        ? `${needs.length} ${plural(
            needs.length,
            "meal has",
            "meals have"
          )} no suitable dish and ${plural(
            needs.length,
            "needs",
            "need"
          )} you. Open the calendar to choose.`
        : null
    );
    setCopiedFrom((c) => {
      const next = { ...c };
      delete next[String(roomId)];
      return next;
    });
    await load();
  };

  const onSave = async () => {
    if (!draft || !roomId) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    setWarning(null);
    try {
      const body = toBody(draft, false);
      if (!body.enabled) {
        // nothing is filled when switching off, so there is nothing to preview
        await finish(body, null, false);
        return;
      }
      const preview = await previewStanding(roomId!, body);
      if (!preview.changedMeals || preview.changedMeals.length === 0) {
        await finish(body, preview, false);
      } else {
        setPending({ body, preview });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const choose = async (overwrite: boolean) => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await finish(pending.body, pending.preview, overwrite);
      setPending(null);
    } catch (e) {
      setPending(null);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async (toRoomId: number | string) => {
    if (!roomId) return;
    const to = ((customer && customer.rooms) || []).find(
      (r) => String(r.id) === String(toRoomId)
    );
    setError(null);
    try {
      await copyStanding(roomId, toRoomId);
      setCopiedFrom((c) => ({ ...c, [String(toRoomId)]: roomName }));
      const toName = to ? to.name : "the other room";
      setMessage(
        `Copied ${roomName}'s standing order to ${toName}. Open ${toName} and press Save standing order to keep it.`
      );
    } catch (e) {
      setError(e.message);
    }
  };

  const otherRooms = ((customer && customer.rooms) || []).filter(
    (r) => String(r.id) !== String(roomId)
  );

  let body: React.ReactNode;
  if (enabled === null) {
    body = null;
  } else if (!enabled) {
    body = (
      <div className={cn("card")}>
        <p>
          Standing orders are not turned on for your school, so ordering works
          exactly as it always has: open a room, open a day, type your numbers.
        </p>
      </div>
    );
  } else if (loadError) {
    body = <div className={cn("note", "note_error")}>{loadError}</div>;
  } else if (!draft) {
    body = null;
  } else {
    const ticked = MEAL_KEYS.filter(
      (m) => draft.meals.indexOf(m) >= 0 && draft.orderable.indexOf(m) >= 0
    );
    const sharedUsers = ticked.filter((m) => !draft.own[m]);
    const copied = copiedFrom[String(roomId)];

    body = (
      <>
        <div className={cn("topline")}>
          <SelectClassroom />
        </div>
        {error && <div className={cn("note", "note_error")}>{error}</div>}
        {message && <div className={cn("note", "note_info")}>{message}</div>}
        {warning && <div className={cn("note", "note_warn")}>{warning}</div>}
        {copied ? (
          <div className={cn("note", "note_info")}>
            Copied from {copied}. Check the numbers, then press{" "}
            <b>Save standing order</b> to keep them.
          </div>
        ) : (
          dirty && (
            <div className={cn("note", "note_warn")}>
              You have changes that are not saved yet.
            </div>
          )
        )}
        <div className={cn("card")}>
          <Toggle
            checked={draft.enabled}
            label="Use a standing order for this room"
            onChange={() => edit((d) => ({ ...d, enabled: !d.enabled }))}
          />
          {draft.enabled ? (
            <p className={cn("help")}>
              Enter {roomName}'s usual week once. Every day's order is then
              filled in from the published menu. You can still change any day
              by hand.
            </p>
          ) : (
            <p className={cn("help")}>
              The standing order is off for {roomName}. Nothing new will be
              filled in. Days that were already filled in stay as ordinary
              orders you can change.
            </p>
          )}
        </div>
        <div className={cn("card", { dim: !draft.enabled })}>
          <h2>Which meals does {roomName} get?</h2>
          <MealTicks
            ticked={ticked}
            orderable={draft.orderable}
            onToggle={(m) =>
              edit((d) => ({
                ...d,
                meals:
                  d.meals.indexOf(m) >= 0
                    ? d.meals.filter((x) => x !== m)
                    : [...d.meals, m],
              }))
            }
          />
          <h2>
            {sharedUsers.length
              ? `Numbers for ${listAnd(sharedUsers.map((m) => MEAL_NAME[m]))}`
              : "Shared numbers (no meal uses them right now)"}
          </h2>
          <NeedsGrid
            scope="shared"
            grid={draft.shared}
            onChange={(d, n, v) => setCount("shared", d, n, v)}
          />
          {ticked.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {ticked.map((m) => (
                <OwnNumbersSwitch
                  key={m}
                  meal={m}
                  own={draft.own[m]}
                  onUseOwn={() =>
                    edit((d) => ({
                      ...d,
                      own: { ...d.own, [m]: fullGrid(d.shared) },
                    }))
                  }
                  onUseShared={() =>
                    edit((d) => {
                      const own = { ...d.own };
                      delete own[m];
                      return { ...d, own };
                    })
                  }
                  onChange={(d, n, v) => setCount(m, d, n, v)}
                />
              ))}
            </div>
          ) : (
            <p className={cn("help")}>
              Tick at least one meal to order it from this standing order.
            </p>
          )}
        </div>
        <div className={cn("actions")}>
          <CopyToRoom rooms={otherRooms} disabled={busy} onCopy={onCopy} />
          <button
            type="button"
            className={cn("btn", "btn_primary")}
            disabled={busy}
            onClick={onSave}
          >
            Save standing order
          </button>
        </div>
        {pending && (
          <SaveDialog
            preview={pending.preview}
            busy={busy}
            onUpdateThose={() => choose(true)}
            onLeaveThem={() => choose(false)}
          />
        )}
      </>
    );
  }

  return (
    <AsideWrapper>
      <Loader isLoading={enabled === null}>
        <div className="container">
          <div className="max-container">
            <div className={cn("page")}>
              {enabled && <StandingOrderTabs active="standing" />}
              {body}
            </div>
          </div>
        </div>
      </Loader>
    </AsideWrapper>
  );
};

export default StandingOrder;
